import { useEffect, useRef, type RefObject } from "react";

/** Modal dialogs keep keyboard focus inside and return it to the control that opened them. */
export function useDialogFocus(card: RefObject<HTMLDivElement | null>, onClose: () => void) {
  const opener = useRef(document.activeElement as HTMLElement | null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const el = card.current;
    if (!el) return;
    const controls = () => [...el.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex]')]
      .filter((n) => n.tabIndex >= 0 && !n.matches(":disabled") && !n.closest("[hidden], [inert]") && getComputedStyle(n).visibility !== "hidden" && n.getClientRects().length > 0);
    const first = el.querySelector<HTMLElement>("[data-autofocus]") ?? controls().find((n) => !n.classList.contains("close")) ?? el;
    // React's autoFocus may already have focused the search input before this effect.
    if (!el.contains(document.activeElement)) first.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close.current(); }
      if (e.key !== "Tab") return;
      const nodes = controls(), start = nodes[0], end = nodes[nodes.length - 1];
      if (!start) { e.preventDefault(); el.focus(); }
      else if (e.shiftKey && (document.activeElement === start || !nodes.includes(document.activeElement as HTMLElement))) { e.preventDefault(); (end ?? start).focus(); }
      else if (!e.shiftKey && (document.activeElement === end || !nodes.includes(document.activeElement as HTMLElement))) { e.preventDefault(); start.focus(); }
    };
    const contain = (e: FocusEvent) => { if (!el.contains(e.target as Node)) (controls()[0] ?? el).focus(); };
    addEventListener("keydown", key, true);
    addEventListener("focusin", contain);
    return () => {
      removeEventListener("keydown", key, true);
      removeEventListener("focusin", contain);
      if (opener.current?.isConnected) opener.current.focus();
    };
  }, [card]);
}
